"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = __importStar(require("react"));
var createApi_1 = require("./utils/createApi");
var createUseDataLayerApi_1 = require("./utils/createUseDataLayerApi");
var createWindowUtil_1 = require("./utils/createWindowUtil");
var createCopy_1 = require("./utils/createCopy");
var useModifySSRContext_1 = require("./utils/useModifySSRContext");
var traverseSchema_1 = require("./utils/traverseSchema");
var setDeepValue_1 = require("./utils/setDeepValue");
var getDeepValue_1 = require("./utils/getDeepValue");
/**
 * The main API to create a DataLayerProvider and useDataLayerApi hook.
 */
function createDataLayer(
/** Your dataLayer schema, should contain empty strings for not set values */
dataLayer, 
/** The property that it will read/write to on the window object */
windowProperty) {
    if (windowProperty === void 0) { windowProperty = 'dataLayer'; }
    // Utility for working on window.dataLayer safely.
    var windowUtil = createWindowUtil_1.createWindowUtil(windowProperty);
    // If window.dataLayer is not set already, set it now so that the tag manager can start tracking ASAP!
    if (windowUtil.isEmpty()) {
        windowUtil.setValue(dataLayer);
    }
    var ApiContext = react_1.createContext(null);
    // Create a strongly typed DataLayerProvider that knows your schema and will create an api accordingly
    function DataLayerProvider(_a) {
        // SSR context for providing access to dataLayer above.
        var children = _a.children, _b = _a.debug, debug = _b === void 0 ? false : _b;
        var _c = react_1.useMemo(function () {
            var initialDebugMessage = 'Initialized clean state';
            // Check if dataLayer the current dataLayer is the one on window (if not it's been set serverside)
            if (windowUtil.isDifferent(dataLayer)) {
                // Set the value that's been set from serverside
                dataLayer = windowUtil.getValue();
                initialDebugMessage = "Hydrated state from window." + windowProperty;
            }
            else {
                // Create a copy so we don't mutate initial object on SSR state
                dataLayer = createCopy_1.createCopy(dataLayer);
                // Set the dataLayer property on the window
                windowUtil.setValue(dataLayer);
            }
            return {
                // Create the api (dataLayerApi({ ...values })... etc.)
                dataLayerApi: createApi_1.createApi(debug, dataLayer, initialDebugMessage),
                dataLayerRef: dataLayer,
            };
        }, []), dataLayerApi = _c.dataLayerApi, dataLayerRef = _c.dataLayerRef;
        // Apply SSR Context object with functions (getScriptComponent...)
        useModifySSRContext_1.useModifySSRContext(windowProperty, dataLayerRef);
        // Render the dataLayerApi down through context
        return react_1.default.createElement(ApiContext.Provider, { value: dataLayerApi }, children);
    }
    function DataLayerSet(_a) {
        var children = _a.children, values = _a.values, _b = _a.debugMessage, debugMessage = _b === void 0 ? '<DataLayer.Set />' : _b, condition = _a.condition, dependencyArray = _a.dependencyArray, revertWhenUnmounted = _a.revertWhenUnmounted;
        var dataLayerApi = useDataLayerApi();
        var _depArr = dependencyArray;
        if (typeof condition !== 'undefined') {
            _depArr = _depArr ? _depArr.concat([condition]) : [condition];
        }
        react_1.useEffect(function () {
            if (revertWhenUnmounted) {
                var getExistingValues = function (dataLayer, values) {
                    var _prevValues = {};
                    traverseSchema_1.traverseSchema(values, {
                        onContainer: function (keyPath) { return setDeepValue_1.setDeepValue(_prevValues, keyPath, {}); },
                        onValue: function (keyPath) {
                            return setDeepValue_1.setDeepValue(_prevValues, keyPath, getDeepValue_1.getDeepValue(dataLayer, keyPath));
                        },
                    });
                    return _prevValues;
                };
                var prevValues_1 = getExistingValues(dataLayer, values);
                return function () {
                    dataLayerApi(prevValues_1, "Revert: " + debugMessage);
                };
            }
        }, [revertWhenUnmounted]);
        var shouldFire = typeof condition === 'undefined' || condition;
        // If we are in an SSR environment, make sure the api is called outside useEffect
        if (typeof window === 'undefined' && shouldFire) {
            dataLayerApi(values, debugMessage);
        }
        react_1.useEffect(function () {
            if (shouldFire) {
                dataLayerApi(values, debugMessage);
            }
        }, _depArr);
        return react_1.default.createElement(react_1.default.Fragment, null, children);
    }
    var useDataLayerApi = createUseDataLayerApi_1.createUseDataLayerApi(ApiContext);
    return {
        _useRawDataLayer: function () { return windowUtil.getValue(); },
        useApi: useDataLayerApi,
        Provider: DataLayerProvider,
        Set: DataLayerSet,
        SSR: useModifySSRContext_1.DataLayerSSRContext.Provider,
    };
}
exports.createDataLayer = createDataLayer;
